import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useApp, useDocumentTitle } from '../app/context.js';
import { PilotBanner } from '../components/PilotBanner.js';
import { authMessage, safeTarget, type SignInStep } from '../lib/auth.js';

type View = 'signIn' | 'newPassword' | 'totp' | 'forgot' | 'forgotConfirm';

const TITLES: Record<View, { mute: string; main: string }> = {
  signIn: { mute: 'Willkommen', main: 'zurück.' },
  newPassword: { mute: 'Fast geschafft', main: 'Eigenes Passwort' },
  totp: { mute: 'Noch ein Schritt', main: 'Bestätigungscode' },
  forgot: { mute: 'Kein Problem', main: 'Passwort vergessen' },
  forgotConfirm: { mute: 'Code eingeben', main: 'Neues Passwort' },
};

/**
 * Eigene Anmeldeseite im Betriebsam-Design (statt Cognito Managed Login): Anmelden, Erstanmeldung
 * nach Einladung, Zwei-Faktor-Code, Passwort vergessen. Impressum und Datenschutz direkt darunter.
 */
export function SignInPage() {
  useDocumentTitle('Anmelden');
  const { auth, session } = useApp();
  const [params] = useSearchParams();
  const target = safeTarget(params.get('weiter'));
  const [view, setView] = useState<View>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const firstField = useRef<HTMLInputElement>(null);

  // Bereits angemeldet (z. B. zweiter Tab): direkt weiter.
  useEffect(() => {
    let alive = true;
    void auth.hasSession().then((ok) => ok && alive && !import.meta.env.VITE_MOCK && location.replace(target));
    return () => {
      alive = false;
    };
  }, [auth, target]);

  // Am Handy nicht automatisch fokussieren – die Tastatur würde sofort die halbe Seite verdecken.
  useEffect(() => {
    if (window.matchMedia('(pointer: fine)').matches) firstField.current?.focus();
  }, [view]);

  function go(next: View) {
    setView(next);
    setError(null);
    setPassword('');
    setPassword2('');
    setCode('');
  }

  function follow(step: SignInStep) {
    if (step === 'done') {
      location.assign(target);
      return;
    }
    if (step === 'resetPassword') {
      go('forgot');
      setNotice('Bitte setze dein Passwort zurück. Wir schicken dir einen Code per E-Mail.');
      return;
    }
    go(step);
  }

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(authMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const passwordsMatch = password.length > 0 && password === password2;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    switch (view) {
      case 'signIn':
        setNotice(null);
        return void run(async () => follow(await auth.signIn(email, password)));
      case 'newPassword':
        if (!passwordsMatch) return setError('Die beiden Passwörter stimmen nicht überein.');
        return void run(async () => follow(await auth.setNewPassword(password)));
      case 'totp':
        return void run(async () => follow(await auth.confirmTotp(code)));
      case 'forgot':
        return void run(async () => {
          await auth.requestReset(email);
          go('forgotConfirm');
          setNotice(`Falls es ein Konto zu ${email.trim()} gibt, haben wir einen Code dorthin geschickt.`);
        });
      case 'forgotConfirm':
        if (!passwordsMatch) return setError('Die beiden Passwörter stimmen nicht überein.');
        return void run(async () => {
          await auth.confirmReset(email, code, password);
          go('signIn');
          setNotice('Dein Passwort ist geändert. Melde dich jetzt damit an.');
        });
    }
  }

  const t = TITLES[view];
  const emailField = (
    <Field id="email" label="E-Mail">
      <input
        ref={firstField}
        id="email"
        className="input"
        type="email"
        autoComplete="username"
        inputMode="email"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
    </Field>
  );
  const newPasswordFields = (
    <>
      <Field id="pw" label="Neues Passwort" hint="Mindestens 12 Zeichen, Groß- und Kleinbuchstaben und eine Zahl.">
        <PasswordInput inputRef={view === 'newPassword' ? firstField : undefined} id="pw" autoComplete="new-password" value={password} onChange={setPassword} />
      </Field>
      <Field id="pw2" label="Passwort wiederholen">
        <PasswordInput id="pw2" autoComplete="new-password" value={password2} onChange={setPassword2} />
      </Field>
    </>
  );

  return (
    <div className="auth">
      {session.pilot && <PilotBanner />}
      <span className="auth__dot auth__dot--1" aria-hidden="true" />
      <span className="auth__dot auth__dot--2" aria-hidden="true" />
      <span className="auth__dot auth__dot--3" aria-hidden="true" />
      <main className="auth__card">
        <span className="wordmark auth__wordmark">Betriebsam</span>
        <h1 className="page__title auth__title">
          <span className="mute">{t.mute}</span>
          <br />
          {t.main}
        </h1>

        {notice && <div className="alert alert--info" role="status">{notice}</div>}

        <form className="form" onSubmit={submit} noValidate>
          {view === 'signIn' && (
            <>
              {emailField}
              <Field id="password" label="Passwort">
                <PasswordInput id="password" autoComplete="current-password" value={password} onChange={setPassword} />
              </Field>
            </>
          )}

          {view === 'newPassword' && (
            <>
              <p className="note">Willkommen bei Betriebsam! Lege jetzt ein eigenes Passwort fest – das vorläufige aus der Einladung gilt dann nicht mehr.</p>
              {newPasswordFields}
            </>
          )}

          {view === 'totp' && (
            <Field id="code" label="Code aus der Authenticator-App">
              <input
                ref={firstField}
                id="code"
                className="input input--code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={7}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </Field>
          )}

          {view === 'forgot' && (
            <>
              <p className="note">Gib deine E-Mail-Adresse ein. Wir schicken dir einen Code, mit dem du ein neues Passwort festlegst.</p>
              {emailField}
            </>
          )}

          {view === 'forgotConfirm' && (
            <>
              <Field id="code" label="Code aus der E-Mail">
                <input
                  ref={firstField}
                  id="code"
                  className="input input--code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
              </Field>
              {newPasswordFields}
            </>
          )}

          {error && (
            <div className="alert alert--error" role="alert">
              {error}
            </div>
          )}

          <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
            {busy ? 'Einen Moment …' : view === 'signIn' ? 'Anmelden' : view === 'forgot' ? 'Code anfordern' : view === 'totp' ? 'Bestätigen' : 'Passwort speichern'}
          </button>
        </form>

        <div className="auth__links">
          {view === 'signIn' ? (
            <button type="button" className="link-btn" onClick={() => go('forgot')}>
              Passwort vergessen?
            </button>
          ) : (
            <button
              type="button"
              className="link-btn"
              onClick={() => {
                go('signIn');
                setNotice(null);
              }}
            >
              Zurück zur Anmeldung
            </button>
          )}
        </div>

        <LegalLinks className="legal-links legal-links--divided" />
      </main>
    </div>
  );
}

/** Passwortfeld mit „Anzeigen“ und Hinweis auf eine aktive Feststelltaste. */
function PasswordInput({
  id,
  value,
  onChange,
  autoComplete,
  inputRef,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: 'current-password' | 'new-password';
  inputRef?: React.Ref<HTMLInputElement>;
}) {
  const [visible, setVisible] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const checkCaps = (e: React.KeyboardEvent<HTMLInputElement>) => setCapsLock(e.getModifierState?.('CapsLock') ?? false);
  return (
    <>
      <span className="input-wrap">
        <input
          ref={inputRef}
          id={id}
          className="input input--with-toggle"
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={checkCaps}
          onKeyUp={checkCaps}
          onBlur={() => setCapsLock(false)}
          required
        />
        <button type="button" className="input-toggle" aria-controls={id} aria-pressed={visible} onClick={() => setVisible((v) => !v)}>
          {visible ? 'Verbergen' : 'Anzeigen'}
        </button>
      </span>
      {capsLock && <span className="field__hint field__hint--warn">Die Feststelltaste ist aktiv.</span>}
    </>
  );
}

function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {children}
      {hint && <span className="field__hint">{hint}</span>}
    </div>
  );
}

/** Dezente Rechtslinks – auf der Anmeldung, in der Shell und in den Tools. */
export function LegalLinks({ className = 'legal-links' }: { className?: string }) {
  return (
    <nav className={className} aria-label="Rechtliches">
      <Link to="/impressum">Impressum</Link>
      <span aria-hidden="true">·</span>
      <Link to="/datenschutz">Datenschutz</Link>
      <span aria-hidden="true">·</span>
      <Link to="/kontakt">Kontakt</Link>
    </nav>
  );
}
